// Copyright 2026, University of Maryland, All Rights Reserved
import { type FC } from 'react'
import { useQuery } from '@tanstack/react-query'

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/components/ui'
import { Field } from '@/components/forms'
import { apiClient } from '@/app-shell/api/api-client'
import {
  KB_SCHEMAS_API_PATH,
  KB_SCHEMAS_QUERY_KEY,
} from '@/subsystems/knowledge-base/admin/SchemasTab'
import type { KbSchema } from '@/subsystems/knowledge-base/shared/types'

export type KnowledgeBaseMode = 'findings' | 'report' | 'criteria'

export interface KnowledgeBaseSettingsFormValues {
  label: string
  description: string
  mode: KnowledgeBaseMode
  itemSchemaId: string | null
  injectionTemplate: string
}

export interface KnowledgeBaseSettingsFormProps {
  values: KnowledgeBaseSettingsFormValues
  onChange: (next: KnowledgeBaseSettingsFormValues) => void
  disabled?: boolean
  labelInputId?: string
}

const NO_SCHEMA_VALUE = '__none__'

const MODE_HELPER: Record<KnowledgeBaseMode, string> = {
  findings: 'LLM extracts matching text from documents and applies risk scoring.',
  criteria: 'Items define areas of interest for analysis without risk scoring.',
  report: 'LLM produces section content based on the analysis frameworks.',
}

export const KnowledgeBaseSettingsForm: FC<KnowledgeBaseSettingsFormProps> = ({
  values,
  onChange,
  disabled,
  labelInputId = 'kb-settings-label',
}) => {
  const { data: schemas = [], isLoading: schemasLoading } = useQuery<KbSchema[]>({
    queryKey: KB_SCHEMAS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<KbSchema[]>(KB_SCHEMAS_API_PATH)
      return data ?? []
    },
    staleTime: 60_000,
  })

  const update = <K extends keyof KnowledgeBaseSettingsFormValues>(
    key: K,
    next: KnowledgeBaseSettingsFormValues[K],
  ) => onChange({ ...values, [key]: next })

  return (
    <div className="space-y-4">
      <Field label="Name" htmlFor={labelInputId}>
        <Input
          id={labelInputId}
          value={values.label}
          onChange={e => update('label', e.target.value)}
          placeholder="Knowledge base name"
          disabled={disabled}
        />
      </Field>

      <Field
        label="Description"
        helper={<span className="text-sm">Optional summary describing what this knowledge base is for.</span>}
      >
        <Textarea
          value={values.description}
          onChange={e => update('description', e.target.value)}
          placeholder="What is this knowledge base used for?"
          rows={3}
          disabled={disabled}
        />
      </Field>

      <Field
        label="Mode"
        helper={<span className="text-sm">{MODE_HELPER[values.mode]}</span>}
      >
        <Select
          value={values.mode}
          onValueChange={v => update('mode', v as KnowledgeBaseMode)}
          disabled={disabled}
        >
          <SelectTrigger className="w-full min-w-0" data-testid="kb-mode-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="findings">
              Findings — Items are clause patterns with risk scores
            </SelectItem>
            <SelectItem value="report">
              Report — Items are section frameworks without scores
            </SelectItem>
            <SelectItem value="criteria">
              Criteria — Items are analysis criteria without scores
            </SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field
        label="Item Schema"
        helper={
          <span className="text-sm">
            When set, KB items are authored against this schema's fields. Use None for free-form items.
          </span>
        }
      >
        <Select
          value={values.itemSchemaId ?? NO_SCHEMA_VALUE}
          onValueChange={v => update('itemSchemaId', v === NO_SCHEMA_VALUE ? null : v)}
          disabled={disabled || schemasLoading}
        >
          <SelectTrigger className="w-full min-w-0" data-testid="kb-item-schema-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_SCHEMA_VALUE}>None (free-form items)</SelectItem>
            {schemas.map(s => (
              <SelectItem key={s.schemaId} value={s.schemaId}>
                {s.label} <span className="text-xs text-muted-foreground">({s.schemaId})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field
        label="Injection Template"
        helper={
          <span className="text-sm">
            {'Use {{ knowledge_base }} where the formatted items JSON should be inserted. Leave blank to dump items as raw JSON.'}
          </span>
        }
      >
        <Textarea
          value={values.injectionTemplate}
          onChange={e => update('injectionTemplate', e.target.value)}
          placeholder="Leave blank for raw JSON. Use {{ knowledge_base }} as a placeholder for the items."
          rows={4}
          className="font-mono text-sm"
          disabled={disabled}
        />
      </Field>
    </div>
  )
}
